import React, { useState } from "react";
import {
  ArrowRight,
  ArrowDown,
  Check,
  Mail,
  Inbox,
  AlertTriangle,
  Search,
  Ship,
  Container as ContainerIcon,
  ClipboardCheck,
  Anchor,
  PackageCheck,
  Compass,
  Eye,
  Radio,
  Activity,
  Layers,
  Gauge,
  Gavel,
  FileSignature,
  Truck,
  ShieldCheck,
  Zap,
  Workflow,
  Sparkles,
  CornerDownRight,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { FREIGHTIQ as T } from "@/constants/testIds/freightiq";
import { WaitlistDialog } from "@/pages/freightiq/WaitlistDialog";
import { HeroDashboard } from "@/pages/freightiq/HeroDashboard";
import { LiveFreightDashboard } from "@/pages/freightiq/LiveFreightDashboard";

/* ------------------ Reusable ------------------ */
const Container = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-[1320px] px-5 sm:px-6 lg:px-8 xl:px-12 ${className}`}>{children}</div>
);

const Eyebrow = ({ children, dot = true, accent = false }) => (
  <div className="inline-flex items-center gap-2">
    {dot && (
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          accent ? "bg-[#FF6B35]" : "bg-[#0A1128]"
        } fiq-pulse-dot`}
      />
    )}
    <span
      className={`fiq-overline ${
        accent ? "text-[#FF6B35]" : "text-[#0A1128]"
      }`}
    >
      {children}
    </span>
  </div>
);

const SectionHead = ({ eyebrow, headline, sub, accent, align = "left", headlineWidth = "max-w-[17ch]" }) => (
  <div
    className={`max-w-[56rem] ${align === "center" ? "mx-auto text-center" : ""} mb-10 md:mb-14 lg:mb-16`}
  >
    <Eyebrow accent={accent}>{eyebrow}</Eyebrow>
    <h2 className={`font-display mt-4 text-3xl font-bold leading-[1.05] tracking-tight text-[#0A1128] sm:text-4xl lg:text-5xl ${headlineWidth} ${align === "center" ? "mx-auto" : ""}`}>
      {headline}
    </h2>
    {sub && (
      <p className={`mt-4 max-w-[68ch] text-sm leading-7 text-[#475569] sm:text-base md:text-lg ${align === "center" ? "mx-auto" : ""}`}>{sub}</p>
    )}
  </div>
);

/* ------------------ Nav ------------------ */
const Nav = ({ onOpenWaitlist }) => {
  return (
    <nav className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
      <Container className="flex items-center justify-between gap-4 py-3.5 lg:py-4">
        <div className="flex min-w-0 items-center gap-2.5" data-testid={T.navLogo}>
          <div className="w-8 h-8 rounded-md bg-[#0A1128] text-white flex items-center justify-center">
            <Ship className="w-4 h-4 text-[#FF6B35]" />
          </div>
          <div className="min-w-0 leading-tight">
            <div className="truncate font-display text-base font-bold tracking-tight text-[#0A1128]">
              FreightIQ<span className="text-[#FF6B35]">™</span>
            </div>
            <div className="truncate text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500">
              a DeMaxtore workspace
            </div>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-7 xl:gap-8">
          <a data-testid={T.navProblem} href="#problem" className="text-sm text-[#475569] hover:text-[#0A1128] transition-colors">
            The Problem
          </a>
          <a data-testid={T.navHow} href="#how" className="text-sm text-[#475569] hover:text-[#0A1128] transition-colors">
            How it Works
          </a>
          <a data-testid={T.navWhy} href="#why" className="text-sm text-[#475569] hover:text-[#0A1128] transition-colors">
            Why FreightIQ
          </a>
          <a data-testid={T.navFaq} href="#faq" className="text-sm text-[#475569] hover:text-[#0A1128] transition-colors">
            FAQ
          </a>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <button
            data-testid={T.navWorkspaceCta}
            onClick={onOpenWaitlist}
            className="fiq-btn-ghost hidden rounded-xl px-4 py-2 text-sm font-medium md:inline-flex"
          >
            Create Workspace
          </button>
          <button
            data-testid={T.navStartCta}
            onClick={onOpenWaitlist}
            className="fiq-btn-primary inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold sm:px-5"
          >
            Start FreightIQ <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </Container>
    </nav>
  );
};

/* ------------------ Hero ------------------ */
const Hero = ({ onOpenWaitlist }) => {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 fiq-grid-bg opacity-70 pointer-events-none" />
      <div className="pointer-events-none absolute right-[-8rem] top-12 h-[24rem] w-[24rem] rounded-full bg-[#FF6B35]/5 blur-[100px] sm:h-[32rem] sm:w-[32rem]" />

      <Container className="relative pb-16 pt-12 sm:pb-20 sm:pt-16 lg:pb-24 lg:pt-20 xl:pb-28">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-12 xl:gap-16">
          <div className="fiq-fade-up lg:col-span-5 xl:col-span-5">
            <div data-testid={T.heroEyebrow}>
              <Eyebrow accent>WORKSPACE PRODUCT · CARRIER COORDINATION</Eyebrow>
            </div>

            <h1
              data-testid={T.heroHeadline}
              className="mt-5 font-display text-[3.2rem] font-extrabold leading-[0.96] tracking-[-0.045em] text-[#0A1128] sm:text-[4.25rem] lg:text-[5rem] xl:text-[5.6rem]"
            >
              FreightIQ
              <span className="ml-1 align-super text-xl text-[#FF6B35] sm:text-2xl">™</span>
            </h1>

            <div
              data-testid={T.heroSubheadline}
              className="mt-5 max-w-[15ch] font-display text-[2rem] font-bold leading-[1.06] tracking-tight text-[#0A1128] sm:text-[2.35rem] lg:text-[2.7rem]"
            >
              One Shipment.<br />
              One Freight Process.<br />
              <span className="text-[#FF6B35]">Zero Coordination Chaos.</span>
            </div>

            <p
              data-testid={T.heroDescription}
              className="mt-5 max-w-[36rem] text-base leading-8 text-[#475569] sm:text-lg"
            >
              Coordinate freight booking, carrier communication, shipment milestones and
              container movements inside one Workspace.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                data-testid={T.heroPrimaryCta}
                onClick={onOpenWaitlist}
                className="fiq-btn-accent inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-semibold sm:w-auto sm:min-w-[220px]"
              >
                Start FreightIQ™ <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid={T.heroSecondaryCta}
                onClick={onOpenWaitlist}
                className="fiq-btn-ghost inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-semibold sm:w-auto sm:min-w-[240px]"
              >
                Create Your Free Workspace
              </button>
            </div>

            <div
              data-testid={T.heroTrustBar}
              className="mt-8 grid max-w-[38rem] grid-cols-1 gap-x-6 gap-y-3 text-left min-[520px]:grid-cols-2"
            >
              {[
                "Carrier Coordination",
                "Live Shipment Visibility",
                "Ocean Freight Execution",
                "One Workspace",
              ].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-600" strokeWidth={3} />
                  <span className="text-sm font-semibold text-[#0A1128]">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative lg:col-span-7 xl:col-span-7">
            <HeroDashboard testIds={T} />
          </div>
        </div>
      </Container>

      {/* Carrier marquee */}
      <div className="relative overflow-hidden border-y border-slate-200 bg-slate-50/40 py-5 sm:py-6">
        <div className="mx-auto mb-4 flex max-w-[1280px] items-center gap-4 px-5 sm:px-6 lg:px-8 xl:px-10">
          <span className="fiq-overline text-slate-400">Coordinates with the world&rsquo;s ocean carriers</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
        <div className="relative">
          <div className="flex fiq-marquee whitespace-nowrap">
            {[...Array(2)].map((_, k) => (
              <div key={k} className="flex items-center gap-14 px-7">
                {[
                  "MSC",
                  "Maersk",
                  "CMA CGM",
                  "Hapag-Lloyd",
                  "ONE",
                  "Evergreen",
                  "COSCO",
                  "ZIM",
                  "Yang Ming",
                  "HMM",
                ].map((c) => (
                  <span
                    key={`${k}-${c}`}
                    className="font-display text-xl md:text-2xl tracking-tight font-bold text-slate-300"
                  >
                    {c}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------ Problem ------------------ */
const Problem = () => {
  const issues = [
    { i: Inbox, l: "Booking confirmations buried in inbox" },
    { i: Mail, l: "Carrier communication across 14 emails" },
    { i: ContainerIcon, l: "Container updates received hours late" },
    { i: AlertTriangle, l: "ETA changes nobody told operations" },
    { i: FileSignature, l: "Document exchange over WhatsApp" },
    { i: Search, l: "Manual follow-ups every single day" },
  ];

  return (
    <section
      data-testid={T.problemSection}
      id="problem"
      className="border-y border-slate-200 bg-slate-50 py-20 md:py-24 lg:py-28"
    >
      <Container>
        <SectionHead
          eyebrow="THE PROBLEM"
          headline="Freight Coordination Shouldn't Depend On Emails."
          headlineWidth="max-w-[15ch]"
          sub="Every import shipment passes through dozens of touchpoints — bookings, vessels, containers, documents, alerts. Yet most operations still glue it together with inboxes and spreadsheets. FreightIQ replaces that chaos with one Workspace."
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8 xl:gap-10">
          {/* Email chaos */}
          <div
            data-testid={T.problemEmailCard}
            className="relative h-full overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white p-5 sm:p-6 lg:col-span-5 lg:p-7"
          >
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Eyebrow>BEFORE · INBOX CHAOS</Eyebrow>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 fiq-pulse-dot" />
                17 unread
              </span>
            </div>
            <div className="space-y-2">
              {[
                { f: "MSC Booking Desk", s: "Re: Re: Re: SH-2481 vessel confirmation??", n: true },
                { f: "Manufacturer · Acme", s: "Fwd: Container loaded — see WhatsApp pls", n: true },
                { f: "Forwarder", s: "Update: ETA pushed by 4 days. Pls advise.", n: true, w: true },
                { f: "Customs Broker", s: "Need Certificate of Origin TODAY", n: true, w: true },
                { f: "MSC Tracking", s: "Re: Vessel position request for MSCU1234567", n: false },
                { f: "Sales", s: "Customer asking ETA for the 3rd time", n: false },
              ].map((e, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-3 px-3 py-2.5 rounded-md border ${
                    e.w
                      ? "border-amber-200 bg-amber-50/60"
                      : e.n
                        ? "border-slate-200 bg-slate-50"
                        : "border-slate-100 bg-white"
                  }`}
                >
                  <div className="w-1.5 h-1.5 mt-2 rounded-full bg-[#0A1128]" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs font-semibold text-[#0A1128] truncate">{e.f}</span>
                      <span className="text-[10px] font-mono text-slate-400 whitespace-nowrap">
                        {String(8 + i).padStart(2, "0")}:{String(4 + i * 7).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{e.s}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 text-xs text-slate-400 font-mono">
              + 11 more unread · 3 missed follow-ups
            </div>
          </div>

          {/* FreightIQ workspace */}
          <div
            data-testid={T.problemWorkspaceCard}
            className="relative h-full overflow-hidden rounded-[1.5rem] border border-[#0A1128] bg-[#0A1128] p-5 text-white sm:p-6 lg:col-span-7 lg:p-7"
          >
            <div className="absolute inset-0 fiq-grid-bg-dark opacity-50 pointer-events-none" />
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#FF6B35]/15 blur-[100px] pointer-events-none" />
            <div className="relative">
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Eyebrow accent>AFTER · FREIGHTIQ WORKSPACE</Eyebrow>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 fiq-pulse-dot" />
                  all synced
                </span>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 mb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="fiq-overline text-white/40 mb-1">Shipment</div>
                    <div className="font-display font-bold text-xl">SH-2481</div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 fiq-pulse-dot" />
                    On Track
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <div className="text-white/40 text-[10px] font-mono uppercase tracking-wider">Carrier</div>
                    <div className="font-semibold">MSC</div>
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] font-mono uppercase tracking-wider">Milestone</div>
                    <div className="font-semibold">At Sea</div>
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] font-mono uppercase tracking-wider">ETA</div>
                    <div className="font-semibold">12 Jun</div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {[
                  { i: ClipboardCheck, l: "Booking confirmed", t: "auto-logged" },
                  { i: ContainerIcon, l: "Container loaded · MSCU1234567", t: "carrier feed" },
                  { i: Anchor, l: "Vessel departed Mersin", t: "live position" },
                  { i: FileSignature, l: "All documents complete", t: "4 / 5 collected" },
                ].map((row, i) => {
                  const Icon = row.i;
                  return (
                    <div
                      key={i}
                      className="flex flex-col gap-1.5 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon className="w-3.5 h-3.5 text-[#FF6B35]" />
                        <span className="text-xs">{row.l}</span>
                      </div>
                      <span className="text-[10px] font-mono text-white/40">{row.t}</span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 text-xs text-white/60 font-mono">
                Zero emails · zero follow-ups · zero spreadsheets.
              </div>
            </div>
          </div>
        </div>

        {/* Issue grid */}
        <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {issues.map((it, i) => {
            const I = it.i;
            return (
              <div
                key={i}
                className="fiq-card flex h-full flex-col gap-2.5 px-4 py-4"
              >
                <I className="w-4 h-4 text-[#FF6B35]" />
                <div className="text-xs font-medium text-[#0A1128] leading-snug">{it.l}</div>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

/* ------------------ How it Works ------------------ */
const HowItWorks = () => {
  const steps = [
    { i: Gavel, l: "Purchase Order Approved", d: "Imported directly from CommodityBid™." },
    { i: Truck, l: "Carrier Assigned", d: "Pick your carrier or your forwarder — your call." },
    { i: ClipboardCheck, l: "Booking Confirmed", d: "Booking reference, vessel & voyage captured." },
    { i: ContainerIcon, l: "Container Loaded", d: "Container number tied to the shipment." },
    { i: Anchor, l: "Vessel Departed", d: "Real departure event from the carrier feed." },
    { i: Ship, l: "At Sea", d: "Live position, milestones and ETA changes." },
    { i: Compass, l: "Destination Port", d: "Arrival, customs & port-handler updates." },
    { i: PackageCheck, l: "Delivered", d: "Final delivery confirmed, shipment closed." },
  ];

  return (
    <section
      data-testid={T.howSection}
      id="how"
      className="bg-white py-20 md:py-24 lg:py-28"
    >
      <Container>
        <SectionHead
          eyebrow="HOW FREIGHTIQ WORKS"
          headline="One Workflow. From Purchase Order to Delivery."
          headlineWidth="max-w-[16ch]"
          sub="Every step lives inside one Workspace — captured automatically, shared with everyone who needs it, surfaced before it becomes a problem."
        />

        <div className="relative">
          {/* Vertical line behind */}
          <div className="absolute bottom-4 left-5 top-4 w-px bg-slate-200 md:left-1/2 md:-translate-x-1/2" />

          <div className="space-y-4 md:space-y-0">
            {steps.map((s, i) => {
              const Icon = s.i;
              const left = i % 2 === 0;
              return (
                <div
                  key={i}
                  data-testid={T.howStep(i)}
                  className="relative pl-12 md:grid md:grid-cols-2 md:items-center md:gap-8 lg:gap-12 md:pl-0"
                >
                  <div className={`hidden md:block ${left ? "" : "md:col-start-2"}`}>
                    <div
                      className={`fiq-card max-w-lg p-6 lg:p-7 ${
                        left ? "ml-auto" : ""
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-md bg-[#0A1128] text-white flex items-center justify-center">
                          <Icon className="w-4 h-4 text-[#FF6B35]" />
                        </div>
                        <div className="fiq-overline text-slate-400">Step {String(i + 1).padStart(2, "0")}</div>
                      </div>
                      <div className="font-display text-lg font-bold tracking-tight text-[#0A1128]">
                        {s.l}
                      </div>
                      <div className="text-sm text-[#475569] mt-1.5 leading-relaxed">{s.d}</div>
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="absolute left-5 top-8 flex -translate-x-1/2 items-center justify-center md:left-1/2">
                    <span className="relative flex items-center justify-center">
                      <span className="absolute w-6 h-6 rounded-full bg-[#FF6B35]/20 fiq-ping" />
                      <span className="w-3 h-3 rounded-full bg-[#FF6B35] ring-4 ring-white" />
                    </span>
                  </div>

                  {/* Mobile */}
                  <div className="fiq-card md:hidden p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-md bg-[#0A1128] text-white flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[#FF6B35]" />
                      </div>
                      <div>
                        <div className="fiq-overline text-slate-400">Step {String(i + 1).padStart(2, "0")}</div>
                        <div className="font-display text-base font-bold tracking-tight text-[#0A1128]">
                          {s.l}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-[#475569] leading-relaxed">{s.d}</div>
                  </div>

                  {/* Spacer half */}
                  <div className={`hidden md:block ${left ? "" : "md:col-start-1 md:row-start-1"}`} />

                  {/* Vertical spacing between rows */}
                  <div className="md:col-span-2 hidden md:block h-4 lg:h-5" />
                </div>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
};

/* ------------------ Live Dashboard section ------------------ */
const LiveDashboardSection = () => {
  return (
    <section
      data-testid={T.liveDashboardSection}
      id="dashboard"
      className="relative overflow-hidden bg-[#0A1128] py-20 md:py-24 lg:py-28"
    >
      <div className="absolute inset-0 fiq-grid-bg-dark opacity-40 pointer-events-none" />
      <Container className="relative">
        <div className="mb-10 max-w-[48rem] md:mb-14 lg:mb-16">
          <Eyebrow accent>LIVE FREIGHT DASHBOARD</Eyebrow>
          <h2 className="mt-4 max-w-[14ch] font-display text-3xl font-bold leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
            Boardroom-grade visibility on every shipment.
          </h2>
          <p className="mt-4 max-w-[62ch] text-sm leading-7 text-white/65 sm:text-base md:text-lg">
            Booking status, container status, carrier, current milestone, ETA, required documents,
            real-time alerts, shipment health and the full operation timeline — in one place.
            Not a freight marketplace. Not a price comparison. Pure coordination.
          </p>
        </div>

        <LiveFreightDashboard testIds={T} />
      </Container>
    </section>
  );
};

/* ------------------ Why FreightIQ ------------------ */
const WhyFreightIQ = () => {
  const cards = [
    {
      i: Workflow,
      l: "One Carrier Coordination",
      d: "Every carrier, forwarder and manufacturer working from the same shipment record. No more “who’s on the email thread?”",
      stat: "1 source of truth",
    },
    {
      i: Eye,
      l: "Booking Visibility",
      d: "Booking references, vessels, voyages, container numbers — captured the moment they exist.",
      stat: "0 missed bookings",
    },
    {
      i: Radio,
      l: "Live Milestones",
      d: "Loaded, departed, at sea, arrived, delivered. Streamed automatically from carrier feeds.",
      stat: "Real-time updates",
    },
    {
      i: Activity,
      l: "Shipment Health",
      d: "A single composite score per shipment so the team knows what needs attention right now.",
      stat: "98 avg. health",
    },
    {
      i: Zap,
      l: "Real-Time Alerts",
      d: "ETA shifts, document gaps, missed milestones — surfaced to the right people before they hurt.",
      stat: "Alert in <60s",
    },
    {
      i: Layers,
      l: "One Workspace",
      d: "Lives inside the DeMaxtore Workspace next to CommodityBid™, Inspection™ and Live Shipment Tracking™.",
      stat: "Unified ecosystem",
    },
  ];

  return (
    <section
      data-testid={T.whySection}
      id="why"
      className="bg-white py-20 md:py-24 lg:py-28"
    >
      <Container>
        <SectionHead
          eyebrow="WHY FREIGHTIQ"
          headline="Designed for the people who actually move the containers."
          headlineWidth="max-w-[18ch]"
          sub="Not a quoting tool. Not a marketplace. A coordination layer that finally treats freight execution as a first-class operational workflow."
        />

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((c, i) => {
            const Icon = c.i;
            return (
              <div
                key={i}
                data-testid={T.whyCard(i)}
                className="fiq-card group flex h-full flex-col p-6 lg:p-7"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-md bg-[#0A1128] text-white flex items-center justify-center transition-transform group-hover:scale-105">
                    <Icon className="w-5 h-5 text-[#FF6B35]" />
                  </div>
                  <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">
                    {String(i + 1).padStart(2, "0")} / 06
                  </span>
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight text-[#0A1128] mb-2">
                  {c.l}
                </h3>
                <p className="text-sm text-[#475569] leading-relaxed">{c.d}</p>
                <div className="mt-auto pt-5 border-t border-slate-100 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#FF6B35]" />
                  <span className="text-xs font-semibold text-[#0A1128]">{c.stat}</span>
                </div>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

/* ------------------ Connected Workspace ------------------ */
const ConnectedWorkspace = () => {
  const flow = [
    { l: "CommodityBid", trade: true, d: "Buyer awards purchase to manufacturer." },
    { l: "Purchase Order", system: true, d: "PO generated automatically." },
    { l: "FreightIQ", active: true, d: "Carrier coordination & milestones." },
    { l: "Inspection", trade: true, d: "Third-party QC inspection." },
    { l: "Live Shipment Tracking", trade: true, d: "Full visibility to destination." },
    { l: "Delivered", system: true, d: "Shipment closed, audit-ready." },
  ];

  return (
    <section
      data-testid={T.connectedSection}
      className="border-y border-slate-200 bg-slate-50 py-20 md:py-24 lg:py-28"
    >
      <Container>
        <SectionHead
          eyebrow="CONNECTED WORKSPACE"
          headline="FreightIQ doesn't live alone. It lives in DeMaxtore."
          headlineWidth="max-w-[18ch]"
          sub="Every Workspace Product hands off cleanly to the next. From bid to delivery, your import lifecycle stays inside one operating platform."
        />

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {flow.map((n, i) => {
            const isActive = n.active;
            return (
              <div
                key={i}
                data-testid={T.connectedNode(i)}
                className={`relative flex h-full min-h-[220px] flex-col rounded-[1.35rem] p-6 transition-all ${
                  isActive
                    ? "bg-[#0A1128] text-white border border-[#0A1128] shadow-[0_30px_60px_-30px_rgba(10,17,40,0.5)]"
                    : "bg-white border border-slate-200"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span
                    className={`text-[10px] font-mono uppercase tracking-widest ${
                      isActive ? "text-[#FF6B35]" : n.system ? "text-slate-400" : "text-[#FF6B35]"
                    }`}
                  >
                    {n.system ? "system step" : "workspace product"}
                  </span>
                  <span
                    className={`text-[10px] font-mono ${
                      isActive ? "text-white/50" : "text-slate-400"
                    }`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h4
                  className={`font-display text-xl font-bold tracking-tight ${
                    isActive ? "text-white" : "text-[#0A1128]"
                  }`}
                >
                  {n.l}
                  {n.trade && !n.system && <span className={`${isActive ? "text-[#FF6B35]" : "text-[#FF6B35]"}`}>™</span>}
                </h4>
                <p
                  className={`text-sm mt-2 leading-relaxed ${
                    isActive ? "text-white/70" : "text-[#475569]"
                  }`}
                >
                  {n.d}
                </p>
                {i < flow.length - 1 && (
                  <div className="absolute bottom-5 right-5 hidden h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white/95 xl:flex xl:bottom-auto xl:right-[-0.75rem] xl:top-1/2 xl:h-6 xl:w-6 xl:-translate-y-1/2">
                    <CornerDownRight className="w-3 h-3 text-slate-400 rotate-[-45deg]" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </Container>
    </section>
  );
};

/* ------------------ Outcomes ------------------ */
const Outcomes = () => {
  const cards = [
    { v: "−72%", l: "Fewer Follow-ups", d: "Replace status-chasing emails with one shared Workspace." },
    { v: "+3.4×", l: "Better Carrier Coordination", d: "Every carrier conversation tied to a real shipment record." },
    { v: "100%", l: "Complete Shipment Visibility", d: "Every milestone, document and alert in one timeline." },
    { v: "−48h", l: "Faster Issue Resolution", d: "Surface ETA changes and document gaps before customers hear about them." },
    { v: "1", l: "One Freight Workflow", d: "From booking confirmation to final delivery — one process." },
    { v: "1", l: "One Workspace", d: "Plugged into the entire DeMaxtore Operating Platform." },
  ];

  return (
    <section
      data-testid={T.outcomesSection}
      className="bg-white py-20 md:py-24 lg:py-28"
    >
      <Container>
        <SectionHead
          eyebrow="BUSINESS OUTCOMES"
          headline="Measured where it matters: in the operation."
          headlineWidth="max-w-[16ch]"
          sub="Customers using FreightIQ inside the DeMaxtore Workspace consistently report fewer escalations, faster resolutions and a far cleaner audit trail."
        />

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((c, i) => (
            <div
              key={i}
              data-testid={T.outcomeCard(i)}
              className="fiq-card group relative flex h-full flex-col overflow-hidden p-6 lg:p-7"
            >
              <div className="absolute -bottom-12 -right-12 w-32 h-32 rounded-full bg-[#FF6B35]/0 group-hover:bg-[#FF6B35]/8 transition-colors blur-2xl" />
              <div className="relative">
                <div className="fiq-overline text-slate-400 mb-3">Outcome {String(i + 1).padStart(2, "0")}</div>
                <div className="mb-3 font-display text-4xl font-extrabold leading-none tracking-[-0.04em] text-[#0A1128] sm:text-5xl">
                  {c.v}
                </div>
                <h4 className="font-display text-lg font-bold tracking-tight text-[#0A1128]">
                  {c.l}
                </h4>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{c.d}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="fiq-card mt-10 flex flex-col items-start gap-5 p-6 md:flex-row md:items-center md:gap-8 md:p-7">
          <div className="flex items-center gap-3">
            <Gauge className="w-6 h-6 text-[#FF6B35]" />
            <div className="font-display text-lg font-bold text-[#0A1128]">
              Built for enterprise operations.
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#475569]">
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> SOC 2-aligned controls</span>
            <span className="flex items-center gap-2"><Workflow className="w-4 h-4 text-emerald-600" /> Carrier-agnostic</span>
            <span className="flex items-center gap-2"><Layers className="w-4 h-4 text-emerald-600" /> One audit-ready record</span>
          </div>
        </div>
      </Container>
    </section>
  );
};

/* ------------------ FAQ ------------------ */
const FAQ = () => {
  const items = [
    {
      q: "How does FreightIQ work?",
      a: "FreightIQ takes over the moment a Purchase Order is awarded. You assign your carrier (or your forwarder), capture the booking, and FreightIQ pulls in vessel, container, milestone and document data — automatically surfacing it to everyone on the shipment inside one Workspace.",
    },
    {
      q: "Can I use my own freight forwarder?",
      a: "Yes. FreightIQ is carrier- and forwarder-agnostic. Bring your existing forwarder relationships — FreightIQ becomes the coordination layer on top of them, not a replacement.",
    },
    {
      q: "Can I work with different ocean carriers?",
      a: "Absolutely. FreightIQ coordinates across MSC, Maersk, CMA CGM, Hapag-Lloyd, ONE, Evergreen, COSCO, ZIM and more. You always choose the carrier — FreightIQ orchestrates the coordination.",
    },
    {
      q: "Will I receive shipment updates?",
      a: "Yes. Container loading, vessel departure, position updates, ETA shifts, document completion and arrival events are streamed into your Workspace in real time, with alerts when something needs your attention.",
    },
    {
      q: "Can I manage multiple containers?",
      a: "Yes. A single FreightIQ shipment can hold one container, a full FCL set or a multi-container booking. Each container is tracked individually with its own milestone timeline.",
    },
    {
      q: "Does FreightIQ connect with the DeMaxtore Workspace?",
      a: "FreightIQ is a Workspace Product inside DeMaxtore. It connects natively with CommodityBid™, Inspection™ and Live Shipment Tracking™ — sharing the same buyer, supplier, PO and shipment records.",
    },
  ];

  return (
    <section
      data-testid={T.faqSection}
      id="faq"
      className="border-y border-slate-200 bg-slate-50 py-20 md:py-24 lg:py-28"
    >
      <Container className="max-w-[980px]">
        <SectionHead
          eyebrow="FREQUENTLY ASKED"
          headline="Everything operations teams ask before they switch."
          align="left"
          headlineWidth="max-w-[18ch]"
        />

        <Accordion type="single" collapsible className="space-y-3">
          {items.map((it, i) => (
            <AccordionItem
              key={i}
              value={`q-${i}`}
              data-testid={T.faqItem(i)}
              className="rounded-[1.15rem] border border-slate-200 bg-white px-5 transition-colors [&[data-state=open]]:border-[#0A1128]"
            >
              <AccordionTrigger className="font-display text-base md:text-lg font-semibold tracking-tight text-[#0A1128] hover:no-underline py-5 text-left">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm md:text-base text-[#475569] leading-relaxed pb-5">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Container>
    </section>
  );
};

/* ------------------ Final CTA ------------------ */
const FinalCTA = ({ onOpenWaitlist }) => {
  return (
    <section
      data-testid={T.finalCta}
      className="bg-white py-16 md:py-20 lg:py-24"
    >
      <Container>
        <div className="relative overflow-hidden rounded-[2rem] bg-[#0A1128] p-6 sm:p-8 md:p-12 lg:p-14 xl:p-16">
          <div className="absolute inset-0 fiq-grid-bg-dark opacity-40 pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-[440px] h-[440px] rounded-full bg-[#FF6B35]/20 blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-32 -left-32 w-[440px] h-[440px] rounded-full bg-[#FF6B35]/10 blur-[120px] pointer-events-none" />

          <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="text-white lg:col-span-7">
              <Eyebrow accent>READY TO COORDINATE</Eyebrow>
              <h2 className="mt-4 max-w-[15ch] font-display text-3xl font-extrabold leading-[1.02] tracking-[-0.04em] sm:text-4xl md:text-5xl lg:text-[5.4rem]">
                Coordinate Every Shipment With Confidence.
              </h2>
              <p className="mt-4 max-w-[38rem] text-sm leading-7 text-white/72 sm:text-base md:text-lg">
                Manage freight execution, carrier communication and shipment milestones inside one Workspace.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button
                  data-testid={T.finalCtaPrimary}
                  onClick={onOpenWaitlist}
                  className="fiq-btn-accent inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-semibold sm:w-auto sm:min-w-[220px]"
                >
                  Start FreightIQ™ <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  data-testid={T.finalCtaSecondary}
                  onClick={onOpenWaitlist}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-semibold text-[#0A1128] transition-colors hover:bg-slate-100 sm:w-auto sm:min-w-[240px]"
                >
                  Create Your Free Workspace
                </button>
              </div>

              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/60">
                <span>One Workspace.</span>
                <span>Complete Freight Coordination.</span>
                <span>No Operational Chaos.</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl sm:p-6 lg:p-7">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Eyebrow accent>workspace preview</Eyebrow>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 fiq-pulse-dot" />
                    Live
                  </span>
                </div>
                <div className="space-y-3">
                  {[
                    { l: "Booking · MSC", v: "Confirmed" },
                    { l: "Container · MSCU1234567", v: "Loaded" },
                    { l: "Vessel · MSC AURIGA", v: "At Sea" },
                    { l: "Documents", v: "4 / 5" },
                  ].map((r, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3"
                    >
                      <span className="text-xs text-white/80">{r.l}</span>
                      <span className="text-xs font-semibold text-white">{r.v}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-2 rounded-lg border border-[#FF6B35]/30 bg-[#FF6B35]/10 px-4 py-3">
                  <ArrowDown className="w-3.5 h-3.5 text-[#FF6B35]" />
                  <span className="text-[11px] text-white/80">
                    Your workspace will look exactly like this — provisioned in minutes.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

/* ------------------ Footer ------------------ */
const Footer = () => {
  return (
    <footer data-testid={T.footer} className="bg-white border-t border-slate-200">
      <Container className="py-12 md:py-14">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-[#0A1128] text-white flex items-center justify-center">
                <Ship className="w-4 h-4 text-[#FF6B35]" />
              </div>
              <div className="font-display text-base font-bold tracking-tight text-[#0A1128]">
                FreightIQ<span className="text-[#FF6B35]">™</span>
              </div>
            </div>
            <p className="mt-4 text-sm text-[#475569] max-w-md leading-relaxed">
              FreightIQ™ is a Workspace Product of <span className="font-semibold text-[#0A1128]">DeMaxtore</span> — the Import Operating Platform for buyers, manufacturers and ocean carriers.
            </p>
          </div>
          <div>
            <div className="fiq-overline text-slate-400 mb-3">Workspace Products</div>
            <ul className="space-y-2 text-sm text-[#475569]">
              <li>CommodityBid™</li>
              <li className="text-[#0A1128] font-semibold">FreightIQ™</li>
              <li>Inspection™</li>
              <li>Live Shipment Tracking™</li>
            </ul>
          </div>
          <div>
            <div className="fiq-overline text-slate-400 mb-3">Resources</div>
            <ul className="space-y-2 text-sm text-[#475569]">
              <li>Platform</li>
              <li>Security</li>
              <li>Carrier Network</li>
              <li>Contact</li>
            </ul>
          </div>
        </div>
        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-slate-200 pt-6 md:flex-row md:items-center">
          <div className="text-xs text-slate-400 font-mono">
            © {new Date().getFullYear()} DeMaxtore. All rights reserved.
          </div>
          <div className="text-xs text-slate-400 font-mono">
            One Workspace · Complete Freight Coordination · No Operational Chaos.
          </div>
        </div>
      </Container>
    </footer>
  );
};

/* ------------------ Page ------------------ */
export default function FreightIQPage() {
  const [open, setOpen] = useState(false);
  const onOpen = () => setOpen(true);

  return (
    <div className="min-h-screen bg-white text-[#0A1128]" data-testid={T.waitlistTrigger + "-page"}>
      <Nav onOpenWaitlist={onOpen} />
      <Hero onOpenWaitlist={onOpen} />
      <Problem />
      <HowItWorks />
      <LiveDashboardSection />
      <WhyFreightIQ />
      <ConnectedWorkspace />
      <Outcomes />
      <FAQ />
      <FinalCTA onOpenWaitlist={onOpen} />
      <Footer />
      <WaitlistDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
